import { computed, useSignal } from '@preact/signals'
import { useCallback, useEffect } from 'preact/hooks'

import { projectsState } from 'state'
import {
	Button,
	ComboBoxAutocomplete,
	Input,
	Modal,
	TextLink,
	Typography
} from 'components/UI'

import {
	smartContractName,
	dAppName,
	resetFormAndErrors,
	setSmartContractName,
	setDAppName,
	createSmartContract,
	inputErrors
} from './state'

const dAppsName = computed(() =>
	projectsState.value.map(project => ({
		name: project.name,
		id: project.publicId
	}))
)

const isButtonDisabled = computed(() => {
	const errors = inputErrors.value

	return (
		!!errors.smart_contract_name ||
		!!errors.dapp_name ||
		!smartContractName.value ||
		!dAppName.value
	)
})

type Props = {
	isOpen: boolean
	onClose: () => void
	defaultDAppName?: {
		id: string
		name: string
	}
	isDAppFieldDisabled?: boolean
}

const NewSmartContractModal = ({
	isOpen,
	onClose,
	defaultDAppName,
	isDAppFieldDisabled
}: Props) => {
	const creatingSmartContract = useSignal(false)
	const errorMessage = useSignal('')

	useEffect(() => {
		if (defaultDAppName) {
			setDAppName(defaultDAppName)
		}
	}, [defaultDAppName])

	const handleCloseModalProject = () => {
		resetFormAndErrors()
		onClose()
	}

	const handleCreateSmartContract = useCallback(
		async (e: Event) => {
			e.preventDefault()
			errorMessage.value = ''
			creatingSmartContract.value = true

			const { error } = await createSmartContract()

			creatingSmartContract.value = false

			if (error) {
				errorMessage.value = error.message

				if (error.code === 'project.unauthorized_action') {
					errorMessage.value =
						'You have reached the maximum number of creations available in your plan, if you want to create more please upgrade your plan.'
				}

				return
			}

			handleCloseModalProject()
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	return (
		<Modal
			bannerErrorMessage={errorMessage.value}
			isOpen={isOpen}
			onCloseModal={handleCloseModalProject}
		>
			<div class='mb-10 px-10 pt-10'>
				<Typography
					color='text-product'
					text='New Smart Contract'
					variant='h2'
				/>
				<Typography
					class='mt-5'
					color='text-black-200'
					text='AI Support Available'
					variant='text2'
				/>

				<form onSubmit={handleCreateSmartContract}>
					<div class='mb-10 mt-10 space-y-10'>
						<Input
							error={inputErrors.value?.smart_contract_name}
							hasError={!!inputErrors.value?.smart_contract_name}
							helperText='The name can only contain letters, numbers, and hyphens.'
							id='smart_contract_name'
							label='Give a name to the smart contract'
							name='smart_contract_name'
							onInput={setSmartContractName}
							placeholder='nft-marketplace'
							value={smartContractName}
						/>

						{defaultDAppName ||
						isDAppFieldDisabled ||
						dAppsName.value.length === 0 ? (
							<Input
								disabled={isDAppFieldDisabled}
								error={inputErrors.value?.dapp_name}
								hasError={
									!!inputErrors.value?.dapp_name && !isDAppFieldDisabled
								}
								helperText={
									defaultDAppName
										? ''
										: 'No existing dApps. Create one here to associate with your Smart Contract.'
								}
								id='dapp_name'
								label={
									defaultDAppName
										? 'Assign the smart contract to the current dApp.'
										: 'Create a new dApp for this Smart Contract'
								}
								name='dapp_name'
								onInput={(value: string) =>
									setDAppName({ id: null, name: value })
								}
								placeholder='Enter dApp name'
								readOnly={!!defaultDAppName}
								value={dAppName.value?.name || ''}
							/>
						) : (
							<ComboBoxAutocomplete
								error={inputErrors.value?.dapp_name}
								hasError={!!inputErrors.value?.dapp_name}
								helperText='Every smart contract must be associated with a dApp.'
								label='Assign the smart contract to a dApp.'
								options={dAppsName.value}
								placeholder='Choose an existing dApp or create a new dApp'
								setSelected={setDAppName}
								subLabel='Choose an existing dApp, or create a new dApp when you click save.'
								value={dAppName.value}
							/>
						)}
					</div>

					<div class='mt-5 flex items-center justify-between'>
						<TextLink
							ariaLabel='Close modal'
							onClick={handleCloseModalProject}
							size='small'
							text='Cancel'
							variant='secondary'
						/>

						<Button
							disabled={creatingSmartContract.value || isButtonDisabled.value}
							isLoading={creatingSmartContract.value}
							text='Save & Open Workspace'
							type='submit'
						/>
					</div>
				</form>
			</div>
		</Modal>
	)
}

export { NewSmartContractModal }
